import React, { useState } from 'react'
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js'
import { Label } from '@components/Form/Label'
import { apiPublicClient } from '@app/services/apiPublicClient'
import { endpoints } from '@app/api/config'
import { AxiosError, AxiosResponse } from 'axios'
import { InputFeedback } from '@components/Basic/InputFeedback'
import {
  CardBoxInputGroup,
  CardFieldsContainer,
  CardFieldsWrapper,
  CardItemNumberWrapper,
  CreditCardRow,
  stripeElementOptions,
} from './styled'
import { BoxWrapperWithSideImage } from '@features/billing/CreditCardBox/BoxWrapperWithSideImage'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { LoaderWrapperStyled } from '@components/FastAppSurvey/styled'
import { Loader } from '@components/Basic/Loader'
import { GrAmex } from 'react-icons/gr'
import { SiDiscover, SiJcb, SiVisa } from 'react-icons/si'
import { BsFillCreditCardFill } from 'react-icons/bs'
import { FaCcDinersClub } from 'react-icons/fa'
import { RiMastercardFill } from 'react-icons/ri'
import { trackEvent } from '@app/dataTracking'
import { makePricingTrackingStep } from '@app/dataTracking/utils'
import { apiAuthClient } from '@app/services/apiAuthClient'
import {
  ApiSetupPaymentIntentResponse,
  ApiUserPricing,
  CreatePaymentIntentResponse,
} from 'BackendApi'
import { PaymentMethod } from '@stripe/stripe-js/types/api/payment-methods'
import * as stripeJs from '@stripe/stripe-js'

interface Props {
  planId?: string
  onCreditCardTokenRetrieved: (creditCardToken: string) => void
  onStripeSubmit?: () => void
  userBillingInfo: ApiUserPricing | undefined
  children: (renderProps: { isLoading: boolean }) => React.ReactNode
}

const Heading = styled.div`
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
`

export const StripeForm: React.FC<Props> = ({
  planId,
  onCreditCardTokenRetrieved,
  onStripeSubmit,
  userBillingInfo,
  children,
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingFormField, setIsLoadingFormField] = useState(true)
  const [ccProviderType, setCcProviderType] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [focusElement, setFocusElement] = useState<
    'cardNumber' | 'expiration' | 'cvc' | null
  >(null)

  const handleError = (errorMessage?: string, errorCode?: string | number) => {
    const computedMessage =
      errorMessage || t('billing.payment.stripeUnhandledPaymentErrorMessage')
    trackEvent({
      eventName: 'CreditCardChangeFailure',
      feature: 'Pricing',
      step: makePricingTrackingStep(location.pathname),
      params: {
        custom: {
          errorCode,
          errorDescription: computedMessage,
        },
      },
      sendEventToIntercom: true,
    })
    setErrorMessage(computedMessage)
    setIsLoading(false)
  }

  const handleSuccess = (creditCardToken: string) => {
    trackEvent({
      eventName: 'CreditCardChangeSuccess',
      feature: 'Pricing',
      step: makePricingTrackingStep(location.pathname),
    })

    onCreditCardTokenRetrieved(creditCardToken)
  }

  const handleLaravelSubmit = async (
    stripeRequestId: string,
    paymentMethod: PaymentMethod,
    stripe: stripeJs.Stripe
  ) => {
    // here we need to set up the payment intent and understand if 3DS is required
    // or if we can save the card info (encrypted token)
    const paymentIntent = await apiAuthClient
      .post(
        endpoints.setupPaymentIntentV2,
        JSON.stringify({
          paymentMethodToken: paymentMethod.id,
        }),
        {
          headers: {
            requestid: stripeRequestId,
          },
        }
      )
      .then(
        (response: AxiosResponse<ApiSetupPaymentIntentResponse>) =>
          response.data.data
      )
      .catch((e: AxiosError) => {
        handleError(
          e.response?.data?.errorMessage ||
            t('billing.payment.genericCantSetupPaymentIntent'),
          e.response?.data?.errorCode
        )
      })

    if (!paymentIntent) {
      return
    }

    if (
      paymentIntent.requiresAction &&
      paymentIntent.paymentIntentClientSecret
    ) {
      // payment required a 3DS flow
      const process3DS = await stripe.confirmCardSetup(
        paymentIntent.paymentIntentClientSecret
      )
      if (process3DS.error || !process3DS.setupIntent) {
        handleError(process3DS.error.message)
        return
      }
      handleSuccess(paymentMethod.id)
    } else if (paymentIntent.paymentIntentId) {
      // no 3D flow is required
      handleSuccess(paymentMethod.id)
    } else {
      // paymentIntent token/id
      handleError('Missing token')
    }
  }

  const handleLegacySubmit = async (
    stripeRequestId: string,
    paymentMethod: PaymentMethod,
    stripe: stripeJs.Stripe
  ) => {
    // here we need to set up the payment intent and understand if 3DS is required
    // or if we can save the card info (encrypted token)
    const paymentIntent = await apiPublicClient
      .post(
        endpoints.createPaymentIntent,
        JSON.stringify({
          payment_method_id: paymentMethod.id, // from stripe `createPaymentMethod()` response
          planId: planId || '', // can be empty if it is not checkout page
        }),
        {
          headers: {
            requestid: stripeRequestId,
          },
        }
      )
      .then(
        (response: AxiosResponse<CreatePaymentIntentResponse>) => response.data
      )
      .catch((e: AxiosError) => {
        handleError(
          e.response?.data?.errorMessage ||
            t('billing.payment.genericCantSetupPaymentIntent'),
          e.response?.data?.errorCode
        )
      })

    if (!paymentIntent) {
      return
    }

    if (
      paymentIntent.requires_action &&
      paymentIntent.payment_intent_client_secret
    ) {
      // payment required a 3DS flow
      const process3DS = await stripe.confirmCardSetup(
        paymentIntent.payment_intent_client_secret
      )
      if (process3DS.error || !process3DS.setupIntent) {
        handleError(process3DS.error.message)
        return
      }
      handleSuccess(process3DS.setupIntent.id)
    } else if (paymentIntent.payment_intent_id) {
      // no 3D flow is required
      handleSuccess(paymentIntent.payment_intent_id)
    } else {
      // paymentIntent token/id
      handleError('Missing token')
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    if (onStripeSubmit) {
      onStripeSubmit()
    }
    event.preventDefault()
    setIsLoading(true)
    setErrorMessage(null)

    const stripeRequestId = process.env.REACT_APP_STRIPE_REQUEST_ID
    if (!elements || !stripeRequestId) {
      handleError(t('billing.payment.stripeMissinRequestId'))
      return
    }

    // getting stripe element
    const cardElement = elements.getElement(CardNumberElement)
    if (!cardElement || !stripe) {
      handleError(t('billing.payment.stripeCantLoadElement'))
      return
    }

    // create the payment by passing the stripe elements
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    })

    if (error) {
      handleError(error.message)
      return
    }

    if (!paymentMethod) {
      // this should never happen, since if `paymentMethod` is not available
      // stripe will return the error object handled above
      handleError('Invalid payment method')
      return
    }

    await handleLaravelSubmit(stripeRequestId, paymentMethod, stripe)

    setIsLoading(false)
  }

  const setCardProviderIcon = () => {
    /*
    BRAND ALLOWED BY STRIPE API:
    amex
    cartes_bancaires
    diners_club
    discover
    jcb
    mastercard
    visa
    unionpay
    */
    switch (ccProviderType) {
      case 'visa':
        return <SiVisa size={28} />
      case 'mastercard':
        return <RiMastercardFill size={28} />
      case 'amex':
        return <GrAmex size={28} />
      case 'discover':
        return <SiDiscover size={28} />
      case 'diners_club':
        return <FaCcDinersClub size={28} />
      case 'jcb':
        return <SiJcb size={28} />
      default:
        return <BsFillCreditCardFill size={21} />
    }
  }

  const component = (
    <>
      <CardFieldsContainer>
        {isLoadingFormField ? (
          <div className={'loader-container-form'}>
            <LoaderWrapperStyled>
              <Loader $active $size={'small'} $text={'Loading...'} />
            </LoaderWrapperStyled>
          </div>
        ) : (
          <></>
        )}
        <CardFieldsWrapper isLoading={isLoadingFormField}>
          <CreditCardRow>
            <CardBoxInputGroup hasFocus={focusElement === 'cardNumber'}>
              <Label $label={t('billing.payment.cardNumber')} />

              <CardItemNumberWrapper className={'card-item'}>
                <div className={'card-provider'}>{setCardProviderIcon()}</div>
                <CardNumberElement
                  className={`card-item-number`}
                  options={stripeElementOptions}
                  onFocus={() => {
                    setFocusElement('cardNumber')
                  }}
                  onChange={(p) => {
                    setCcProviderType(p.brand)
                  }}
                  onBlur={() => {
                    setFocusElement(null)
                  }}
                />
              </CardItemNumberWrapper>
            </CardBoxInputGroup>
          </CreditCardRow>
          <CreditCardRow>
            <CardBoxInputGroup hasFocus={focusElement === 'expiration'}>
              <Label $label={t('billing.payment.cardExpiration')} />
              <CardExpiryElement
                className='card-item card-item-expiry'
                options={stripeElementOptions}
                onFocus={() => {
                  setFocusElement('expiration')
                }}
                onReady={() => {
                  setIsLoadingFormField(false)
                }}
                onBlur={() => {
                  setFocusElement(null)
                }}
              />
            </CardBoxInputGroup>
            <CardBoxInputGroup hasFocus={focusElement === 'cvc'}>
              <Label $label={t('billing.payment.cardCVC')} />
              <CardCvcElement
                className='card-item card-item-cvc'
                options={stripeElementOptions}
                onFocus={() => {
                  setFocusElement('cvc')
                }}
                onReady={() => {
                  setIsLoadingFormField(false)
                }}
                onBlur={() => {
                  setFocusElement(null)
                }}
              />
            </CardBoxInputGroup>
          </CreditCardRow>
        </CardFieldsWrapper>
      </CardFieldsContainer>
    </>
  )

  return (
    <form onSubmit={handleSubmit}>
      <>
        <>{component}</>
        {errorMessage ? (
          <InputFeedback $status={{ error: errorMessage }} />
        ) : null}
      </>

      {/* submit buttons and checkout info */}
      <div>{children({ isLoading })}</div>
    </form>
  )
}
